import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart2,
  Briefcase,
  Calendar,
  Clipboard,
  DollarSign,
  Eye,
  FileText,
  Home,
  Map,
  Package,
  Phone,
  Search,
  ShoppingCart,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
  Sliders,
  Plus,
  X,
  RotateCcw,
  Check,
} from "react-feather";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Badge, Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import "./ReportsDashboard.css";
import { isSystemConfigEnabled } from "../../../sharedFunctions/SystemConfig";
import { getProfileValues, replacePlaceholders, normalizeUrl } from "../../../sharedFunctions/customTabs";

// Clean custom bookmark/pin icon SVG
function PinIcon({
  pinned,
  onClick,
  className,
  size = 14,
}: {
  pinned: boolean;
  onClick?: (e?: any) => void;
  className?: string;
  size?: number;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      stroke="currentColor"
      strokeWidth="2"
      fill={pinned ? "currentColor" : "none"}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default", transition: "all 0.2s ease" }}
    >
      {/* Standard Board Pushpin Icon */}
      <path d="M12 2v20M17 5H7M15 5v7.586a1 1 0 0 0 .293.707l2.828 2.829a1 1 0 0 1-.707 1.707H6.586a1 1 0 0 1-.707-1.707l2.828-2.829a1 1 0 0 0 .293-.707V5" />
    </svg>
  );
}

// Cleaner sort indicator chevron icon
function SortIndicator({
  active,
  direction,
}: {
  active: boolean;
  direction: "asc" | "desc";
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="11"
      height="11"
      stroke="currentColor"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginLeft: 6, verticalAlign: "middle", display: "inline-block" }}
    >
      <polyline points="7 15 12 20 17 15" style={{ opacity: active && direction === "desc" ? 1 : 0.4 }} />
      <polyline points="7 9 12 4 17 9" style={{ opacity: active && direction === "asc" ? 1 : 0.4 }} />
    </svg>
  );
}

type Frequency = "Daily" | "Weekly" | "Monthly" | "MTD" | "YTD" | "FTD" | "Date-wise";

interface ReportCatalogItem {
  name: string;
  description: string;
  frequency: Frequency;
  icon: React.ReactNode;
  url: string;
}

interface ReportItem extends ReportCatalogItem {
  pinned: boolean;
}

interface StatItem {
  label: string;
  value: string;
  tone: "blue" | "orange" | "green" | "purple";
  icon: React.ReactNode;
}

const reportCatalog: ReportCatalogItem[] = [
  // {
  //   name: "Attendance Summary",
  //   description: "Employee attendance summary report",
  //   frequency: "Monthly",
  //   icon: <Calendar size={15} />,
  //   url: "http://20.193.128.240/field/attendancesummary/index.php?employee_code={employee_code}",
  // },
  // {
  //   name: "DCR Summary",
  //   description: "Daily call record summary report",
  //   frequency: "Daily",
  //   icon: <Clipboard size={15} />,
  //   url: "http://20.193.128.240/field/dcrsummary/index.php?employee_code={employee_code}",
  // },
  // {
  //   name: "Dr 360",
  //   description: "Doctor 360 degree performance report",
  //   frequency: "Monthly",
  //   icon: <Users size={15} />,
  //   url: "http://20.193.128.240/field/dr360/?employee_code={employee_code}",
  // },
  {
    name: "Effort",
    description: "Field effort tracking report",
    frequency: "Monthly",
    icon: <Zap size={15} />,
    url: "/effortReport",
  },
  {
    name: "Support Capture",
    description: "Support capture summary",
    frequency: "Monthly",
    icon: <Phone size={15} />,
    url: "/supportCaptureSummary",
  },
  {
    name: "Daily Activity",
    description: "Field force daily routine",
    frequency: "Daily",
    icon: <Activity size={15} />,
    url: "/darReport",
  },
  {
    name: "Input Distribution",
    description: "SGPI output distribution",
    frequency: "Monthly",
    icon: <Package size={15} />,
    url: "/sgpiOutReport",
  },
  {
    name: "RCPA Summary",
    description: "Relative competitor product analysis",
    frequency: "Monthly",
    icon: <BarChart2 size={15} />,
    url: "/rcpaSkuSummary",
  },
  {
    name: "Orders",
    description: "Secondary order tracking",
    frequency: "Monthly",
    icon: <ShoppingCart size={15} />,
    url: "/podReport",
  },
  {
    name: "Joint Work",
    description: "Manager joint field work",
    frequency: "Monthly",
    icon: <Briefcase size={15} />,
    url: "/jwReport",
  },
  {
    name: "Daily Joint Work",
    description: "Daily joint field work log",
    frequency: "Monthly",
    icon: <Briefcase size={15} />,
    url: "/dailyjwReport",
  },
  {
    name: "Expenses Summary - Data Validate",
    description: "Expense distribution summary & data validation",
    frequency: "Monthly",
    icon: <DollarSign size={15} />,
    url: "/expensesSummary",
  },
  {
    name: "Expense Status - Data Validate",
    description: "Claims & approval data validation",
    frequency: "Monthly",
    icon: <FileText size={15} />,
    url: "/expensesStatus",
  },
  {
    name: "Employee Attendance Report",
    description: "Employee attendance log",
    frequency: "Date-wise",
    icon: <Calendar size={15} />,
    url: "/employeeAttendaceReport",
  },
  {
    name: "MTP Summary",
    description: "Monthly tour plan recap",
    frequency: "Monthly",
    icon: <Calendar size={15} />,
    url: "/mtpSummary",
  },
  {
    name: "Team Attendance",
    description: "Reportee attendance roll-up",
    frequency: "Date-wise",
    icon: <Users size={15} />,
    url: "/teamAttendanceReport",
  },
  {
    name: "Target Vs Achievement",
    description: "Sales performance index",
    frequency: "Monthly",
    icon: <TrendingUp size={15} />,
    url: "/TargetVsAchievement",
  },
  {
    name: "Leave Balance",
    description: "Earned & casual balance",
    frequency: "YTD",
    icon: <Calendar size={15} />,
    url: "/leaveBalanceReport",
  },
  {
    name: "Daily Effort Report",
    description: "Day-wise field effort details",
    frequency: "Date-wise",
    icon: <Activity size={15} />,
    url: "/dailyEffortReport",
  },
  {
    name: "Attendance Register Details",
    description: "Detailed attendance register",
    frequency: "Monthly",
    icon: <Clipboard size={15} />,
    url: "/reports/common/attendance-register-details",
  },
  {
    name: "Employee Activity Performance",
    description: "Activity-wise performance log",
    frequency: "Date-wise",
    icon: <Users size={15} />,
    url: "/reports/common/employee-activity-performance",
  },
  {
    name: "Product Wise Support",
    description: "Support data by product",
    frequency: "Monthly",
    icon: <Package size={15} />,
    url: "/reports/common/product-wise-support",
  },
  {
    name: "Primary Vs Secondary",
    description: "Primary & secondary sales comparison",
    frequency: "Monthly",
    icon: <BarChart2 size={15} />,
    url: "/reports/common/primary-vs-secondary",
  },
  {
    name: "Visit Activity",
    description: "Field visit activity log",
    frequency: "Monthly",
    icon: <Map size={15} />,
    url: "/reports/common/visit-activity",
  },
  // {
  //   name: "Sample Report",
  //   description: "Sample distribution report",
  //   frequency: "Monthly",
  //   icon: <FileText size={15} />,
  //   url: "/reports/common/sample-report",
  // },
  {
    name: "Call Average Report",
    description: "Daily call average analysis",
    frequency: "Date-wise",
    icon: <Phone size={15} />,
    url: "/reports/common/call-average",
  },
  {
    name: "Target Achievement Report",
    description: "Target vs actual achievement",
    frequency: "Monthly",
    icon: <Target size={15} />,
    url: "/reports/common/target-achievement",
  },
  {
    name: "Employee Claim Report",
    description: "Employee expense claims",
    frequency: "Monthly",
    icon: <DollarSign size={15} />,
    url: "/reports/common/employee-claim",
  },
  {
    name: "RCPA Report",
    description: "Competitor product analysis",
    frequency: "Monthly",
    icon: <BarChart2 size={15} />,
    url: "/reports/common/rcpa-report",
  },
  {
    name: "Pending DCR Report",
    description: "Pending daily call records",
    frequency: "Monthly",
    icon: <Clipboard size={15} />,
    url: "/reports/common/pending-dcr-report",
  },
  {
    name: "Mass Report V2",
    description: "Monthly activity summary v2",
    frequency: "Monthly",
    icon: <FileText size={15} />,
    url: "/reports/common/get-Mas-Report-V2",
  },
  {
    name: "Dar Report V2",
    description: "Daily activity report v2",
    frequency: "Date-wise",
    icon: <Clipboard size={15} />,
    url: "/reports/common/get-Dar-Report-V2",
  },
  {
    name: "Sgpi Out Report V2",
    description: "SGPI output report v2",
    frequency: "Date-wise",
    icon: <Package size={15} />,
    url: "/reports/common/get-Sgpi-Out-Report-V2",
  },
  {
    name: "Expenses Report",
    description: "Detailed expense report",
    frequency: "Monthly",
    icon: <DollarSign size={15} />,
    url: "/reports/common/expenses-report",
  },
  {
    name: "Expenses Summary",
    description: "Expense summary overview",
    frequency: "Monthly",
    icon: <DollarSign size={15} />,
    url: "/reports/common/expenses-summary",
  },
  {
    name: "MAS Report V3",
    description: "Monthly activity summary v3",
    frequency: "Date-wise",
    icon: <FileText size={15} />,
    url: "/masReportV3",
  },
  {
    name: "Outlet View Report",
    description: "Outlet view summary report",
    frequency: "Date-wise",
    icon: <FileText size={15} />,
    url: "/outletViewReport",
  },
  {
    name: "JW Report V2",
    description: "Manager joint field work v2",
    frequency: "Date-wise",
    icon: <Briefcase size={15} />,
    url: "/jwReportV2",
  },
];

const frequencyOrder: Record<Frequency, number> = {
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
  MTD: 4,
  YTD: 5,
  FTD: 6,
  "Date-wise": 7,
};

const getFrequencyClass = (frequency: Frequency) =>
  `reports-frequency reports-frequency-${frequency.toLowerCase().replace("-", "")}`;

function StatCard({ stat }: { stat: StatItem }) {
  return (
    <div className="reports-stat-card">
      <div>
        <span className="reports-stat-label">{stat.label}</span>
        <h3>{stat.value}</h3>
        <div className="reports-stat-trend">
          <TrendingUp size={12} />
          <span>+4.2%</span>
          <small>vs last month</small>
        </div>
      </div>
      <div className={`reports-stat-icon reports-tone-${stat.tone}`}>
        {stat.icon}
      </div>
    </div>
  );
}

function FrequencyBadge({ frequency }: { frequency: Frequency }) {
  return <span className={getFrequencyClass(frequency)}>{frequency}</span>;
}

function PinnedReportCard({
  report,
  onTogglePin,
  onView,
}: {
  report: ReportItem;
  onTogglePin: (name: string) => void;
  onView: (url: string) => void;
}) {
  return (
    <div className="reports-pinned-card">
      <div className="reports-pinned-head">
        <span className="reports-table-icon">{report.icon}</span>
        <PinIcon
          pinned={true}
          className="reports-pin-active"
          onClick={() => onTogglePin(report.name)}
        />
      </div>
      <div className="reports-pinned-body">
        <h6>{report.name}</h6>
        <p>{report.description}</p>
      </div>
      <div className="reports-pinned-foot">
        <FrequencyBadge frequency={report.frequency} />
        <button
          className="reports-link-button"
          type="button"
          onClick={() => onView(report.url)}
        >
          View
          <span>→</span>
        </button>
      </div>
    </div>
  );
}

function ReportRow({
  report,
  onTogglePin,
  onView,
}: {
  report: ReportItem;
  onTogglePin: (name: string) => void;
  onView: (url: string) => void;
}) {
  return (
    <tr>
      <td className="reports-pin-cell">
        <PinIcon
          pinned={report.pinned}
          className={report.pinned ? "reports-pin-active" : "reports-pin-muted"}
          onClick={() => onTogglePin(report.name)}
        />
      </td>
      <td>
        <div className="reports-name-cell">
          <span className="reports-table-icon">{report.icon}</span>
          <div>
            <h6>{report.name}</h6>
            <p>{report.description}</p>
          </div>
        </div>
      </td>
      <td>
        <FrequencyBadge frequency={report.frequency} />
      </td>
      <td className="text-end">
        <button
          className="reports-view-button"
          type="button"
          onClick={() => onView(report.url)}
        >
          <Eye size={13} />
          View
        </button>
      </td>
    </tr>
  );
}

const NIL_REPORT_URLS = [
  "http://20.193.128.240/field/attendancesummary/index.php?employee_code={employee_code}",
  "http://20.193.128.240/field/dcrsummary/index.php?employee_code={employee_code}",
  "http://20.193.128.240/field/dr360/?employee_code={employee_code}",
];

const DEFAULT_ACTIVE_REPORT_URLS = reportCatalog.map((r) => r.url);

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const isNilReportsEnabled = isSystemConfigEnabled("nil_reports") || localStorage.getItem("nil_reports") === "true";

  const [activeReportUrls, setActiveReportUrls] = useState<string[]>(() => {
    try {
      const isCustomized = localStorage.getItem("reports_dashboard_customized") === "true";
      const saved = localStorage.getItem("reports_dashboard_active_reports");
      if (isCustomized && saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Error loading active reports:", e);
    }
    return DEFAULT_ACTIVE_REPORT_URLS;
  });

  useEffect(() => {
    const isCustomized = localStorage.getItem("reports_dashboard_customized") === "true";
    if (!isCustomized) {
      setActiveReportUrls(DEFAULT_ACTIVE_REPORT_URLS);
      localStorage.setItem("reports_dashboard_active_reports", JSON.stringify(DEFAULT_ACTIVE_REPORT_URLS));
    }
  }, []);

  const [pinnedReports, setPinnedReports] = useState<string[]>(() => {
    const saved = localStorage.getItem("reports_dashboard_pinned");
    return saved ? JSON.parse(saved) : [];
  });

  const [isConfigModalOpen, setIsConfigModalOpen] = useState<boolean>(false);

  // Modal dual-box state
  const [modalActiveUrls, setModalActiveUrls] = useState<string[]>([]);
  const [availableSearch, setAvailableSearch] = useState<string>("");
  const [activeSearch, setActiveSearch] = useState<string>("");

  const handleOpenConfigModal = () => {
    setModalActiveUrls([...activeReportUrls]);
    setAvailableSearch("");
    setActiveSearch("");
    setIsConfigModalOpen(true);
  };

  const [sortConfig, setSortConfig] = useState<{
    key: "name" | "frequency" | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });

  const togglePin = (name: string) => {
    const updated = pinnedReports.includes(name)
      ? pinnedReports.filter((n) => n !== name)
      : [...pinnedReports, name];
    setPinnedReports(updated);
    localStorage.setItem("reports_dashboard_pinned", JSON.stringify(updated));
  };

  const handleView = async (url: string) => {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      try {
        const profileValues = await getProfileValues();
        let targetUrl = replacePlaceholders(url, profileValues);
        targetUrl = normalizeUrl(targetUrl);
        window.open(targetUrl, "_blank");
      } catch (e) {
        console.error("Error opening external report:", e);
        window.open(url, "_blank");
      }
    } else {
      navigate(url);
    }
  };

  // Derive reports with their pinned state and active filter
  const reports = useMemo(() => {
    return reportCatalog
      .filter((item) => {
        // Only show active reports configured for the dashboard
        if (!activeReportUrls.includes(item.url)) {
          return false;
        }
        if (item.url === "/TargetVsAchievement" || item.name === "Target Vs Achievement") {
          return isSystemConfigEnabled("targets_vs_achivements_enabled");
        }
        if (item.url === "/supportCaptureSummary" || item.name === "Support Capture") {
          return isSystemConfigEnabled("support_capture");
        }
        if (item.url === "/podReport" || item.name === "Orders") {
          return isSystemConfigEnabled("capture_pod");
        }
        return true;
      })
      .map((item) => ({
        ...item,
        pinned: pinnedReports.includes(item.name),
      }));
  }, [pinnedReports, activeReportUrls]);

  // Modal Dual-Box derived lists
  const modalAvailableReports = useMemo(() => {
    return reportCatalog.filter((item) => {
      if (modalActiveUrls.includes(item.url)) return false;
      if (!availableSearch) return true;
      const q = availableSearch.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.description.toLowerCase().includes(q);
    });
  }, [modalActiveUrls, availableSearch]);

  const modalActiveReports = useMemo(() => {
    return reportCatalog.filter((item) => {
      if (!modalActiveUrls.includes(item.url)) return false;
      if (!activeSearch) return true;
      const q = activeSearch.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.description.toLowerCase().includes(q);
    });
  }, [modalActiveUrls, activeSearch]);

  const handleActivateReport = (url: string) => {
    if (!modalActiveUrls.includes(url)) {
      setModalActiveUrls([...modalActiveUrls, url]);
    }
  };

  const handleDeactivateReport = (url: string) => {
    setModalActiveUrls(modalActiveUrls.filter((u) => u !== url));
  };

  const handleActivateAll = () => {
    setModalActiveUrls(reportCatalog.map((r) => r.url));
  };

  const handleResetToDefault = () => {
    setModalActiveUrls([...DEFAULT_ACTIVE_REPORT_URLS]);
    localStorage.removeItem("reports_dashboard_customized");
    toast.info(`Reset to default all ${DEFAULT_ACTIVE_REPORT_URLS.length} reports`);
  };

  const handleSaveConfig = () => {
    setActiveReportUrls(modalActiveUrls);
    try {
      localStorage.setItem("reports_dashboard_active_reports", JSON.stringify(modalActiveUrls));
      localStorage.setItem("reports_dashboard_customized", "true");
      toast.success(`Updated! Showing ${modalActiveUrls.length} reports.`);
    } catch (e) {
      console.error("Error saving reports config:", e);
    }
    setIsConfigModalOpen(false);
  };

  // Derived filtered reports based on search term
  const filteredReports = useMemo(() => {
    return reports.filter(
      (report) =>
        report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [reports, searchTerm]);

  // Handle column header click for sorting
  const handleSort = (key: "name" | "frequency") => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sorted reports list — pinned rows always float to the top
  const sortedReports = useMemo(() => {
    const sortable = [...filteredReports];

    const compareByConfig = (a: ReportItem, b: ReportItem): number => {
      if (sortConfig.key === "frequency") {
        const orderA = frequencyOrder[a.frequency] || 99;
        const orderB = frequencyOrder[b.frequency] || 99;
        return sortConfig.direction === "asc"
          ? orderA - orderB
          : orderB - orderA;
      } else if (sortConfig.key === "name") {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return sortConfig.direction === "asc" ? -1 : 1;
        if (nameA > nameB) return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    };

    sortable.sort((a, b) => {
      // Pinned rows come first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      // Within same pin group, apply column sort
      return compareByConfig(a, b);
    });

    return sortable;
  }, [filteredReports, sortConfig]);

  const pinnedReportsList = useMemo(() => {
    return reports.filter((report) => report.pinned);
  }, [reports]);

  const statItems = useMemo<StatItem[]>(() => {
    return [
      {
        label: "Total Reports",
        value: String(reports.length),
        tone: "blue",
        icon: <FileText size={18} />,
      },
      {
        label: "Pinned Reports",
        value: String(pinnedReportsList.length),
        tone: "orange",
        icon: <PinIcon pinned={true} size={18} />,
      },
      {
        label: "Daily Reports",
        value: String(reports.filter((r) => r.frequency === "Daily").length),
        tone: "green",
        icon: <Activity size={18} />,
      },
      {
        label: "Monthly Reports",
        value: String(reports.filter((r) => r.frequency === "Monthly").length),
        tone: "purple",
        icon: <Calendar size={18} />,
      },
    ];
  }, [reports, pinnedReportsList]);

  return (
    <div className="reports-dashboard page-body">
      <div className="reports-shell">
        <div className="reports-header">
          <div>
            <div className="reports-breadcrumb">
              <Home size={13} />
              <span>/</span>
              <span>Reports</span>
            </div>
            <h4>Reports</h4>
            <p>Quickly access and manage all available reports</p>
          </div>
          <div className="reports-search">
            <Search size={15} />
            <input
              type="search"
              placeholder="Search report by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="reports-stat-grid">
          {statItems.map((stat) => (
            <StatCard key={stat.label} stat={stat} />
          ))}
        </div>

        <section className="reports-section">
          <div className="reports-section-title">
            <Star size={16} />
            <h6>Pinned Reports</h6>
            <span>({pinnedReportsList.length})</span>
          </div>
          <div className="reports-pinned-grid">
            {pinnedReportsList.map((report) => (
              <PinnedReportCard
                key={report.name}
                report={report}
                onTogglePin={togglePin}
                onView={handleView}
              />
            ))}
          </div>
        </section>

        <section className="reports-section">
          {/* <div className="reports-table-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <h6 className="mb-0">All Reports</h6>
              <span className="badge bg-light text-secondary border">{filteredReports.length} reports</span>
            </div>
            <button
              type="button"
              className="reports-config-btn shadow-sm py-1 px-3 d-flex align-items-center gap-1"
              onClick={handleOpenConfigModal}
              title="Configure report visibility"
            >
              <Sliders size={13} />
              <span>Configure Visibility</span>
            </button>
          </div> */}
          <div className="reports-table-card">
            <div className="table-responsive">
              <table className="table reports-table mb-0">
                <thead>
                  <tr>
                    <th>PIN</th>
                    <th
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      Report Name{" "}
                      <SortIndicator
                        active={sortConfig.key === "name"}
                        direction={sortConfig.direction}
                      />
                    </th>
                    <th
                      onClick={() => handleSort("frequency")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      Frequency{" "}
                      <SortIndicator
                        active={sortConfig.key === "frequency"}
                        direction={sortConfig.direction}
                      />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReports.map((report) => (
                    <ReportRow
                      key={report.name}
                      report={report}
                      onTogglePin={togglePin}
                      onView={handleView}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Configuration Button */}
        <div className="reports-config-trigger-wrapper text-end my-2">
          <button
            type="button"
            className="reports-config-btn shadow-sm"
            onClick={handleOpenConfigModal}
            title="Configure report visibility"
          >
            <Sliders size={13} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Configure Reports Visibility
          </button>
        </div>

        {/* Dual-Box Reports Visibility Configuration Modal */}
        <Modal
          size="xl"
          isOpen={isConfigModalOpen}
          toggle={() => setIsConfigModalOpen(!isConfigModalOpen)}
          centered
          className="reports-config-modal"
        >
          <ModalHeader toggle={() => setIsConfigModalOpen(false)} className="border-bottom pb-2">
            <div className="d-flex align-items-center gap-2">
              <Sliders size={20} className="text-primary" />
              <div>
                <h5 className="mb-0 fw-bold">Manage Reports Visibility</h5>
                <span className="text-muted small">Choose which reports appear on the Reports Dashboard</span>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-3">
            <Row className="g-3">
              {/* Box 1: Available Reports */}
              <Col md={6}>
                <div className="reports-config-box">
                  <div className="reports-config-box-header">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark">All Available Reports</span>
                      <Badge color="secondary" pill>{modalAvailableReports.length}</Badge>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-primary p-0 text-decoration-none fw-semibold"
                      onClick={handleActivateAll}
                    >
                      Activate All
                    </button>
                  </div>
                  <div className="p-2 border-bottom bg-light">
                    <Input
                      type="search"
                      placeholder="Search available reports..."
                      bsSize="sm"
                      value={availableSearch}
                      onChange={(e) => setAvailableSearch(e.target.value)}
                    />
                  </div>
                  <div className="reports-config-box-body">
                    {modalAvailableReports.length === 0 ? (
                      <div className="text-center text-muted py-5 small">
                        No available reports remaining.
                      </div>
                    ) : (
                      modalAvailableReports.map((report) => (
                        <div
                          key={report.url}
                          className="reports-config-item reports-config-item-available"
                          onClick={() => handleActivateReport(report.url)}
                        >
                          <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                            <span className="text-muted">{report.icon}</span>
                            <div className="text-truncate">
                              <div className="fw-semibold text-dark small text-truncate">{report.name}</div>
                              <div className="text-muted" style={{ fontSize: "11px" }}>{report.frequency}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary py-0 px-2 d-flex align-items-center gap-1"
                            style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActivateReport(report.url);
                            }}
                          >
                            <Plus size={12} />
                            <span>Add</span>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </Col>

              {/* Box 2: Active Reports */}
              <Col md={6}>
                <div className="reports-config-box">
                  <div className="reports-config-box-header">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark">Active Reports (Showing)</span>
                      <Badge color="primary" pill>{modalActiveReports.length}</Badge>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0 text-decoration-none fw-semibold"
                      onClick={() => setModalActiveUrls([])}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="p-2 border-bottom bg-light">
                    <Input
                      type="search"
                      placeholder="Search active reports..."
                      bsSize="sm"
                      value={activeSearch}
                      onChange={(e) => setActiveSearch(e.target.value)}
                    />
                  </div>
                  <div className="reports-config-box-body">
                    {modalActiveReports.length === 0 ? (
                      <div className="text-center text-muted py-5 small">
                        No active reports selected.
                      </div>
                    ) : (
                      modalActiveReports.map((report) => (
                        <div
                          key={report.url}
                          className="reports-config-item reports-config-item-active"
                          onClick={() => handleDeactivateReport(report.url)}
                        >
                          <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                            <span className="text-primary">{report.icon}</span>
                            <div className="text-truncate">
                              <div className="fw-semibold text-dark small text-truncate">{report.name}</div>
                              <div className="text-muted" style={{ fontSize: "11px" }}>{report.frequency}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger py-0 px-2 d-flex align-items-center gap-1"
                            style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeactivateReport(report.url);
                            }}
                          >
                            <X size={12} />
                            <span>Remove</span>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter className="d-flex justify-content-between">
            <Button
              color="outline-warning"
              size="sm"
              onClick={handleResetToDefault}
              className="d-flex align-items-center gap-1"
            >
              <RotateCcw size={14} />
              <span>Reset to Default All ({DEFAULT_ACTIVE_REPORT_URLS.length})</span>
            </Button>
            <div className="d-flex gap-2">
              <Button color="secondary" size="sm" onClick={() => setIsConfigModalOpen(false)}>
                Cancel
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={handleSaveConfig}
                className="d-flex align-items-center gap-1 px-3"
              >
                <Check size={14} />
                <span>Save Changes</span>
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
}
